/* eslint-disable no-console */

'use client';

import clsx from 'clsx';
import { isNonEmptyString } from 'tempest-common';
import { useEvent } from '@/utils/hooks/use-event';
import { useCommerceItem } from '@/context/CommerceItemContext';
import { useCommerceConfig } from '@/context/CommerceConfigContext';
import {
	useRef,
	type FC,
	useState,
	useEffect,
	useCallback,
	type ReactNode,
	type MouseEvent,
} from 'react';
import {
	SelectOfferActionType,
	selectSingleOfferReducer,
	type SelectSingleOfferState,
} from '@/components/Commerce/reducers';
import {
	getLinkWrapperFns,
	permutiveKeyValueMapper,
	getDynamicAttributesMapper,
} from '@/components/Commerce/CommerceLink/util';
import {
	NcaUrlEvent,
	swapAffiliateTags,
	buildLinkWrapConfigsForOffer,
	isLinkWrapVendorEnabledInConfig,
} from '@/utils/commerce';
import {
	isCommerceOffer,
	type CommerceItem,
	applyLinkWrappers,
	isLinkEmbedConfig,
	type CommerceOffer,
	buildExternalOffer,
	type LinkEmbedConfig,
	buildMappedValueObject,
} from '@themaven-net/commerce-shared';

export interface CommerceLinkProps {
	children: ReactNode;
	className?: string;
	commerceConfig?: LinkEmbedConfig;
	commerceItem?: CommerceItem;
	destination?: CommerceOffer | string;
	linkTarget?: string;
	phxTrackId?: string;
	rel?: string;
}

const NCA_TIMEOUT = 500;
const FALLBACK_URL = '#';

function buildManualDestination(
	destination: CommerceOffer | string | undefined,
): CommerceOffer | undefined {
	if (destination === undefined) {
		return undefined;
	}

	return isCommerceOffer(destination)
		? destination
		: buildExternalOffer(destination);
}

export const CommerceLink: FC<CommerceLinkProps> = ({
	children,
	className,
	commerceConfig,
	commerceItem,
	destination,
	linkTarget,
	phxTrackId,
	rel,
}) => {
	const commerceConfigContext = useCommerceConfig();
	const commerceItemContext = useCommerceItem();
	const initialItem = commerceItem ?? commerceItemContext?.commerceItem;
	const manualDestination = buildManualDestination(destination);
	const anchorRef = useRef<HTMLAnchorElement>(null);
	const [ncaUrlState, setNcaUrlState] = useState<string>();

	const InitialOfferState: SelectSingleOfferState = {
		offer: manualDestination ?? initialItem?.offers?.[0],
		offerMetadata: {
			preferredAffiliates:
				commerceConfigContext?.phxCommerce?.preferredAffiliates ?? [],
			preferredMerchants:
				commerceConfigContext?.phxCommerce?.preferredMerchants ?? [],
		},
	};

	/**
	 * This timeout is crucial for the integration with Amazon's Native Commerce
	 * Affiliate (NCA) script to function correctly. It addresses a race condition
	 * between the React component rendering and the external Amazon script's
	 * execution.
	 *
	 * How it works:
	 * 1. The CommerceLink component renders an anchor tag (`<a>`).
	 * 2. An external, asynchronously loaded Amazon NCA script scans the page for
	 *    eligible affiliate links.
	 * 3. When it finds one, it may perform its own async operations (like an API
	 *    call) to generate a tracked affiliate URL.
	 * 4. It then dispatches a custom 'nca-url' event on the anchor element,
	 *    carrying the new URL.
	 *
	 * The Problem (Race Condition):
	 * Without a delay, our component might execute `updateWrappedHref` and set the
	 * final `href` on the anchor tag immediately after rendering. This can happen
	 * *before* the Amazon script has had time to find the link, generate the NCA
	 * URL, and dispatch the event. The result would be that we use the original,
	 * non-affiliate URL.
	 *
	 * The Solution:
	 * The `setTimeout` introduces a brief delay (currently 500ms). This "grace
	 * period" gives the external script enough time to complete its work and update
	 * our component's state via the event listener (`handleNcaEvent`) *before* we
	 * finalize the link's `href`. When `updateWrappedHref` eventually runs, it
	 * will have the latest `ncaUrlState` and can construct the correct affiliate
	 * link.
	 */
	const [wrapTimeoutState, setWrapTimeoutState] = useState(false);
	const timeoutRef = useRef<ReturnType<typeof setTimeout>>();

	// Offer effect
	const [offerState, setOfferState] =
		useState<SelectSingleOfferState>(InitialOfferState);

	const linkWrapperConfigs = useRef(
		buildLinkWrapConfigsForOffer(
			offerState.offer,
			commerceConfig,
			commerceConfigContext,
		),
	);

	const handleLinkClick = (clickEvent: MouseEvent) => {
		const targetElement = clickEvent.target as HTMLElement;
		const anchor = targetElement.closest('a');
		const item = commerceItem ?? commerceItemContext?.commerceItem;

		if (!anchor) {
			return;
		}

		const permutive = commerceConfigContext.permutive || window.permutive;
		const phoenixTrackClickEvent =
			commerceConfigContext.phoenixTrackClickEvent ||
			window.phoenixTrackClickEvent;

		if (typeof phoenixTrackClickEvent === 'function') {
			phoenixTrackClickEvent(anchor, clickEvent);
		}

		if (typeof permutive?.track === 'function') {
			permutive.track(
				'AffiliateLinkClick',
				buildMappedValueObject(
					permutiveKeyValueMapper(offerState.offer?.url || '', item),
				),
			);
		}
	};

	const handleNcaEvent = (ncaEvent: Event) => {
		if (!(ncaEvent instanceof NcaUrlEvent)) {
			console.info(ncaEvent);
			return;
		}

		ncaEvent.preventDefault();

		if (commerceConfigContext?.amazonNca !== true) {
			// don't do anything if NCA isn't enabled
			return;
		}

		setNcaUrlState(
			// Ensure the url has the appropriate tag
			swapAffiliateTags(ncaEvent.ncaUrl, commerceConfigContext.affiliateTags),
		);

		// clean up timeout to prevent rerender
		clearTimeout(timeoutRef.current);
		setWrapTimeoutState(true);
	};

	// NCA Listener effect attach/detach, allows the Amazon NCA hook to inform the component of the existence of an NCA url
	useEvent(anchorRef, NcaUrlEvent.EVENT_NAME, handleNcaEvent);

	const selectOffer = useCallback(() => {
		if (offerState.offer === undefined) {
			// no manual anchor tag, no href, and no supplied item/offer, use # to be safe
			const newOfferState = selectSingleOfferReducer(offerState, {
				payload: { offer: buildExternalOffer('#') },
				type: SelectOfferActionType.SET_OFFER,
			});
			setOfferState(newOfferState);
		}

		linkWrapperConfigs.current = buildLinkWrapConfigsForOffer(
			offerState.offer,
			commerceConfig,
			commerceConfigContext,
		);
	}, [commerceConfig, commerceConfigContext, offerState]);

	/**
	 * Determine the correct URL to use at any point in time.
	 *
	 * This takes url from the nca, offer, or fallback and wraps it in
	 * the appropriate link wrapping.  Skimlinks should always be applied
	 * before trackonomics.
	 *
	 * It also respects the timeout so that the Amazon Native Commerce Ads
	 * external library has time to inspect the url on the anchor and notify
	 * the component if there is an identified commerce ad URL.
	 */
	const [displayUrl, setDisplayUrl] = useState(
		offerState.offer?.url ?? FALLBACK_URL,
	);
	useEffect(() => {
		selectOffer();
		if (timeoutRef.current === undefined) {
			timeoutRef.current = setTimeout(
				() => setWrapTimeoutState(true),
				NCA_TIMEOUT,
			);
		}

		if (!offerState.offer || linkWrapperConfigs.current.length === 0) {
			return;
		}

		const cleanUrl = offerState.offer?.url ?? FALLBACK_URL;

		if (wrapTimeoutState === false) {
			// Respect timeout so NCA can inspect our anchor tag
			console.info(
				'!!??!! NCA Timeout not completed, using clean offer',
				cleanUrl,
				anchorRef.current,
			);

			setDisplayUrl(cleanUrl);
			return;
		}

		let url = cleanUrl;

		if (ncaUrlState !== undefined) {
			console.info(
				'!!??!! NCA url has been set, selecting for wrap',
				offerState.offer?.url,
				ncaUrlState,
			);
			url = ncaUrlState;
		}

		if (url === FALLBACK_URL) {
			console.log('!!??!! Fallback URL detected, skipping wrap');
			setDisplayUrl(url);
			return;
		}

		url = applyLinkWrappers(
			url,
			linkWrapperConfigs.current,
			getLinkWrapperFns(commerceConfigContext),
		);

		console.info('!!??!! Setting wrapped URL', url);
		setDisplayUrl(url);
	}, [
		commerceConfigContext,
		ncaUrlState,
		offerState.offer,
		selectOffer,
		wrapTimeoutState,
	]);

	const classNames = clsx(className, {
		noskim: !isLinkWrapVendorEnabledInConfig(
			'Skimlinks',
			linkWrapperConfigs.current,
		),
	});

	return (
		<a
			className={classNames}
			href={displayUrl}
			onClick={handleLinkClick}
			phx-track-id={isNonEmptyString(phxTrackId) ? phxTrackId : undefined}
			ref={anchorRef}
			rel={rel}
			target={linkTarget}
			{...buildMappedValueObject(
				getDynamicAttributesMapper(offerState.offer?.url || ''),
			)}
		>
			{children}
		</a>
	);
};
/**
 * Render a commerce link element using the supplied config
 *
 * This method allows for easy addition of defaults to link configs
 */
export function renderCommerceLink<ChildType extends ReactNode>(
	children: ChildType,
	linkDestination: CommerceOffer | string | undefined,
	item?: CommerceItem,
	classes?: string,
	phxTrackId?: string,
	commerceConfig?: LinkEmbedConfig,
): ReactNode {
	if (
		linkDestination === undefined &&
		(commerceConfig?.item?.offers === undefined ||
			!commerceConfig.item.offers.every(isCommerceOffer))
	) {
		return children;
	}

	return (
		<CommerceLink
			// eslint-disable-next-line react/no-children-prop
			children={children}
			className={classes ?? ''}
			commerceConfig={commerceConfig}
			commerceItem={item}
			destination={linkDestination}
			linkTarget="_blank"
			phxTrackId={phxTrackId}
			rel="nofollow noreferrer"
		/>
	);
}

export function buildCommerceWrappedImage(
	commerceConfig: LinkEmbedConfig | undefined,
	image: ReactNode,
	href: string,
	linkClass: string,
): ReactNode | undefined {
	// don't override the config with the entered href unless nothing
	const useHref =
		!isLinkEmbedConfig(commerceConfig) ||
		commerceConfig?.item?.offers === undefined ||
		!commerceConfig.item.offers.every(isCommerceOffer);

	return renderCommerceLink(
		image,
		useHref ? href : undefined,
		commerceConfig?.item as CommerceItem,
		linkClass,
		undefined,
		commerceConfig,
	);
}
